import { CSSProperties, forwardRef, HTMLAttributes, memo } from "react";
import classnames from "classnames";
import { formatBytes } from "../../../core";
import { Button } from "../../Button";
import { CheckboxBase } from "../../inputs";
import { LoadingIndicator } from "../../LoadingIndicator";
import { FileType, FileTypeIcon, IconType } from "../../MiscIcons";
import { LinearProgress } from "../../Progress";
import { TrashIcon } from "./icons";

import styles from "./style.module.scss";

export type FileUploadType = "progressBar" | "progressFill"

export type FileUploadItemProps = HTMLAttributes<HTMLDivElement> & {
  file: File
  error?: boolean
  iconType?: IconType
  fileType?: FileType
  progress?: number
  removeFile?: () => void
  videoIconType?: string
  fileUploadType?: FileUploadType
  uploadFileAgain?: () => void
  uploadItemErrorMessage?: string
  uploadItemErrorMessageButton?: string
  nameFileClassName?: string
  sizeFileClassName?: string
  loadingTopClassName?: string
  fileWrapperClassName?: string
  loadingZoneClassName?: string
  trashIconBoxClassName?: string
}

export const FileUploadItem = memo(forwardRef<HTMLDivElement, FileUploadItemProps>((
  {
    file,
    error,
    progress,
    iconType = "fileType",
    fileType = "default",
    className,
    removeFile,
    videoIconType,
    fileUploadType,
    uploadFileAgain,
    nameFileClassName,
    sizeFileClassName,
    loadingTopClassName,
    loadingZoneClassName,
    fileWrapperClassName,
    trashIconBoxClassName,
    uploadItemErrorMessage,
    uploadItemErrorMessageButton,
    ...props
  },
  ref,
) => {
  const inProgress = !!progress && progress > 0 && progress < 1;
  const isComplete = !!progress && progress >= 1;

  return (
    file && (
      <div
        className={classnames(fileWrapperClassName, styles.root, {
          [styles.error]: error,
          [styles.progressBar]: fileUploadType === "progressBar",
          [styles.progressFill]: fileUploadType === "progressFill",
        })}
        style={{ "--width-progress": (progress || 0) * 100 + "%" } as CSSProperties}
        ref={ref}
        {...props}
      >
        <FileTypeIcon
          fileExtension={file.name}
          mimeType={file.type}
          videoIconType={videoIconType}
          fileType={fileType}
          iconType={iconType}
          errorFile={error}
        />
        <div className={classnames(loadingZoneClassName, styles.loadingZone)}>
          <div className={classnames(loadingTopClassName, styles.loadingTop)}>
            <div>
              <p className={classnames(nameFileClassName, styles.nameFile)}>{file.name}</p>
              <div className={classnames(sizeFileClassName, styles.sizeFile)}>
                {error
                  ? <>
                    <p>{uploadItemErrorMessage}</p>
                    <Button
                      buttonType="linkGray"
                      buttonSize="sm"
                      onClick={uploadFileAgain}
                      children={uploadItemErrorMessageButton}
                    />
                  </>
                  : `${formatBytes(file.size)}${fileUploadType === "progressFill" && inProgress ? ` - ${Number(progress! * 100)
                    .toFixed(0)}% uploaded` : ""}`
                }
              </div>
            </div>
            {isComplete && <CheckboxBase checkboxBaseSize="sm" checked={true} />}
            {(progress === 0 || (error && fileUploadType === "progressFill") || (!!progress && progress < 1 && fileUploadType === "progressBar")) && (
              <div className={classnames(trashIconBoxClassName, styles.trashIconBox)}>
                <Button
                  buttonType="tertiaryGray"
                  buttonSize="sm"
                  onClick={removeFile}
                  children={<TrashIcon />}
                />
              </div>
            )}
            {!error && fileUploadType === "progressFill" && inProgress &&
              <LoadingIndicator
                loadingIndicatorType="lineSimple"
                loadingIndicatorSize="sm"
                loadingIndicatorLabel=""
              />
            }
          </div>
          {(!error && fileUploadType === "progressBar" && inProgress) &&
            <LinearProgress
              value={progress || 0}
              label="right"
            />
          }
        </div>
      </div>
    )
  );
}));